import * as React from 'react';

import type { CatalogProfileDetailDto, TjmEntryTargetDto } from '@/api/catalog-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RichHtmlView } from '@/components/rich-html-view';
import { cn } from '@/lib/utils';
import type { CorrelatedProfile } from '@/routes/supplier/catalog/catalog-referential-model';

// =============================================================================
// profile-detail-panel.tsx — FICHE DÉTAILLÉE latérale droite (DBU §3.4 l.2724).
// =============================================================================
// DBU §3.4 l.2724 : fiche détaillée = code + libellé, famille/sous-famille, description, compétences
// par niveau, et PROFILS CORRÉLÉS présentés en CHIPS CLIQUABLES. La navigation latérale entre profils
// se fait via ces chips (§3.4 l.2692) : cliquer un chip change le profil affiché (état d'URL `profil`).
//
// Consultation seule (DBU §3.4 l.2730/2736/2738) : AUCUN bouton d'édition / création / archivage,
// AUCUN export — la fiche reste en LECTURE. missions/prerequis `null` sont rendus « Non renseigné »
// (sens doublé par le libellé, jamais une couleur seule) : la fiche n'INVENTE jamais une donnée absente.
//
// R15-CMP-03 (A2 GATE_HUMAN : LIVRER le CTA ; F0-CONTRACT-4 ré-ouverte) : CTA « Saisir mon TJM pour ce
// profil » CONDITIONNEL. Le CTA n'apparaît QUE si le résolveur cross-BC (RESOLVE_TJM_ENTRY_PORT, Published
// Language) retourne une cible non-null — c.-à-d. une campagne OUVERTE POUR SAISIE où le fournisseur EN
// SESSION est invité ET dont ce profil est dans la grille figée. Le clic deep-linke vers SA cellule de
// saisie (`/saisie/$submissionId?profil=<code>`). Aucune cible (fournisseur non invité, profil hors grille,
// campagne fermée) ⇒ AUCUN CTA (la fiche reste en consultation seule). UI 100 % DESIGN SYSTEM (Button DS).
//
// Panneau modal accessible (WCAG 2.1 AA), même patron que rate-collection/profile-detail-dialog :
// role="dialog" + aria-modal, focus déplacé à l'ouverture + restauré à la fermeture, PIÈGE de focus
// (Tab / Shift+Tab bouclent), fermeture au clavier (Échap) + clic overlay (décoratif, aria-hidden).

/** Mention honnête pour un champ facultatif non peuplé (sens doublé par le texte, WCAG 1.4.1). */
const UNFILLED_NOTE = 'Non renseigné';

/**
 * Ordre canonique des niveaux de séniorité (DBU §3.4 l.2724 : « Débutant/Maîtrisant/Expert/Référent »).
 * Le rendu structuré des compétences PAR NIVEAU suit CET ordre (jamais l'ordre de sérialisation JSON).
 */
const SENIORITY_LEVEL_ORDER: readonly string[] = [
  'Débutant',
  'Maîtrisant',
  'Expert',
  'Référent',
];

/** Sélecteur des éléments focusables (piège de focus WCAG 2.1.2 / 2.4.3). */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface ProfileDetailPanelProps {
  /** Profil affiché ; `null` = panneau fermé. */
  readonly profile: CatalogProfileDetailDto | null;
  /** Profils corrélés résolus (chips cliquables), triés par intensité décroissante. */
  readonly correlated: readonly CorrelatedProfile[];
  /** Navigue vers un profil corrélé (met à jour l'état d'URL `profil`). */
  readonly onSelectCorrelated: (profileCode: string) => void;
  /** Ferme le panneau (efface l'état d'URL `profil`). */
  readonly onClose: () => void;
  /**
   * R15-CMP-03 / F0-CONTRACT-4 — cible de saisie TJM résolue pour CE profil (fournisseur en session), ou
   * `null` = pas de CTA. Non-null ⇒ le CTA « Saisir mon TJM pour ce profil » s'affiche (deep-link cellule).
   */
  readonly tjmEntryTarget: TjmEntryTargetDto | null;
  /** R15-CMP-03 — clic sur le CTA : deep-linke vers la cellule de saisie du profil (délégué au routeur). */
  readonly onSaisirTjm: () => void;
}

export function ProfileDetailPanel({
  profile,
  correlated,
  onSelectCorrelated,
  onClose,
  tjmEntryTarget,
  onSaisirTjm,
}: ProfileDetailPanelProps): React.JSX.Element | null {
  const open = profile !== null;
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const root = panelRef.current;
      if (root === null) {
        return;
      }
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (profile === null) {
    return null;
  }

  const titleId = 'catalog-profile-title';
  const descId = 'catalog-profile-subtitle';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative z-10 flex h-full w-full max-w-md flex-col gap-6 overflow-y-auto',
          'border-l border-border bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="font-mono text-xs text-muted-foreground">
              {profile.profileCode}
            </p>
            <h2 id={titleId} className="text-lg font-semibold tracking-tight">
              {profile.libelle}
            </h2>
            <p id={descId} className="text-sm text-muted-foreground">
              {profile.famille} · {profile.sousFamille}
            </p>
          </div>
          <Button ref={closeRef} type="button" variant="outline" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>

        {/* R15-CMP-03 (A2 GATE_HUMAN) — CTA « Saisir mon TJM pour ce profil », CONDITIONNEL au résolveur
            cross-BC (F0-CONTRACT-4). Présent UNIQUEMENT si une campagne ouverte où le fournisseur est invité
            couvre ce profil (grille figée). Le clic deep-linke vers SA cellule de saisie. Button DS (variant
            primary par défaut), aucune UI hors système ; reste dans le piège de focus du dialog. */}
        {tjmEntryTarget !== null && (
          <div>
            <Button type="button" onClick={onSaisirTjm}>
              Saisir mon TJM pour ce profil
            </Button>
          </div>
        )}

        <dl className="space-y-5">
          {/* #14 (DBU §3.4 l.2724 « texte riche ») — description rendue en HTML riche SANITIZÉ
              (RichHtmlView, même sanitizer que l'écriture), plus en texte brut. Vide ⇒ « Non renseigné ». */}
          <div>
            <dt className="text-sm font-medium text-foreground">Description</dt>
            <dd className="mt-1">
              <FieldRichText value={profile.description} />
            </dd>
          </div>

          {/* Niveaux DISPONIBLES (badges) — conservés (availableLevels). Distinct des compétences par niveau. */}
          <div>
            <dt className="text-sm font-medium text-foreground">Niveaux disponibles</dt>
            <dd className="mt-1.5">
              {profile.availableLevels.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {profile.availableLevels.map((level) => (
                    <li key={level}>
                      <Badge variant="secondary">{level}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <UnfilledText />
              )}
            </dd>
          </div>

          {/* #15 (DoD-R3) — Compétences NOMMÉES, structurées PAR NIVEAU (plus seulement les badges de niveaux). */}
          <div>
            <dt className="text-sm font-medium text-foreground">
              Compétences par niveau d'expertise
            </dt>
            <dd className="mt-1.5">
              <CompetencesByLevel value={profile.competencesByLevel} />
            </dd>
          </div>

          {/* #17/#20 — Stacks technologiques (grain famille, cohérence §2.10). */}
          <div>
            <dt className="text-sm font-medium text-foreground">Stacks technologiques</dt>
            <dd className="mt-1.5">
              {profile.stacks.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {profile.stacks.map((stack) => (
                    <li key={stack}>
                      <Badge variant="outline">{stack}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <UnfilledText />
              )}
            </dd>
          </div>

          <Field label="Contextes d'intervention">
            <FieldText value={profile.contextes} />
          </Field>

          <Field label="Missions types">
            <FieldText value={profile.missions} />
          </Field>

          <Field label="Prérequis par niveau">
            <FieldText value={profile.prerequis} />
          </Field>
        </dl>

        <div>
          <h3 className="text-sm font-medium text-foreground">Profils corrélés</h3>
          {correlated.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-2">
              {correlated.map(({ profile: related }) => (
                <li key={related.profileCode}>
                  <button
                    type="button"
                    onClick={() => onSelectCorrelated(related.profileCode)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs text-foreground',
                      'hover:border-primary/60 hover:bg-accent',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    )}
                    aria-label={`Consulter le profil corrélé ${related.libelle}`}
                  >
                    <span className="font-mono text-[0.6875rem] text-muted-foreground">
                      {related.profileCode}
                    </span>
                    <span>{related.libelle}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Aucun profil corrélé.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * #15 (S34-FICHE-PROFONDEUR, DBU §3.4 l.2724) — rend les compétences de référence STRUCTURÉES PAR NIVEAU
 * (Débutant/Maîtrisant/Expert/Référent, ordre canonique), chaque niveau portant ses compétences NOMMÉES.
 * FIN du faux-conforme : la fiche n'affiche plus SEULEMENT des badges de niveaux, mais les compétences
 * réelles par niveau. `null` / aucune compétence ⇒ mention honnête « Non renseigné » (jamais inventé).
 */
function CompetencesByLevel({
  value,
}: {
  readonly value: CatalogProfileDetailDto['competencesByLevel'];
}): React.JSX.Element {
  // Ordre canonique + filtrage des niveaux effectivement porteurs de ≥ 1 compétence.
  const groups =
    value === null
      ? []
      : SENIORITY_LEVEL_ORDER.flatMap((level) => {
          const comps = value[level as keyof typeof value] ?? [];
          return comps.length > 0 ? [{ level, comps }] : [];
        });

  if (groups.length === 0) {
    return <UnfilledText />;
  }

  return (
    <ul className="space-y-3">
      {groups.map(({ level, comps }) => (
        <li key={level}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {level}
          </p>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {comps.map((competence) => (
              <li key={competence}>
                <Badge variant="secondary">{competence}</Badge>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Un champ libellé + contenu de la fiche (dt/dd sémantiques). */
function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <dt className="text-sm font-medium text-foreground">{label}</dt>
      <dd className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
        {children}
      </dd>
    </div>
  );
}

/** Rend un texte facultatif, ou la mention honnête « Non renseigné » (sens doublé par le libellé). */
function FieldText({ value }: { readonly value: string | null }): React.JSX.Element {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return <UnfilledText />;
  }
  return <span>{trimmed}</span>;
}

/**
 * #14 (S34-RICHTEXT-CTA, DBU §3.4 l.2724) — rend un descriptif HTML RICHE sanitizé (RichHtmlView), ou la
 * mention honnête « Non renseigné » si vide. Défense XSS : re-nettoyé au read (allowlist stricte).
 */
function FieldRichText({ value }: { readonly value: string | null }): React.JSX.Element {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return <UnfilledText />;
  }
  return <RichHtmlView html={trimmed} />;
}

/** Mention « Non renseigné » — libellé explicite (jamais un code couleur seul). */
function UnfilledText(): React.JSX.Element {
  return <span className="italic text-muted-foreground">{UNFILLED_NOTE}</span>;
}
